// Reine (React-freie) Tabellen-Logik für die Mapping-Seite — getrennt von der
// Komponente, damit Anker-Aufbau und Filter unabhängig testbar sind.

import type { MappingEntry } from "@/api/mapperApi";

export type RowFilter = "all" | "needs_review";

/** Eine Tabellenzeile: Anzeige-Spalten + Referenz auf den Original-Entry
 * (Letzteres braucht der Edit-Dialog beim Row-Klick). */
export interface MappingRow {
  anchor: string;
  nr: string;
  bo4e: string;
  transform: string;
  status: string;
  entry: MappingEntry;
}

export interface AnchorParts {
  segment_group?: string | null;
  segment: string;
  qualifier?: string | null;
  data_element?: string | null;
}

/** Baut den vollen EDI-Anker-String: `SG2 / NAD +MS  DE3039`.
 * Fehlende/`null`-Teile werden weggelassen; das Segment ist immer dabei. */
export function buildAnchorLabel(a: AnchorParts): string {
  const sg = a.segment_group ? `${a.segment_group} / ` : "";
  const qual = a.qualifier ? ` +${a.qualifier}` : "";
  const de = a.data_element ? `  DE${a.data_element}` : "";
  return `${sg}${a.segment}${qual}${de}`;
}

/** Mappt Mapping-Entries auf Tabellenzeilen (Anzeige-Spalten + Entry-Referenz). */
export function buildMappingRows(entries: MappingEntry[]): MappingRow[] {
  return entries.map((e) => ({
    anchor: buildAnchorLabel(e.edi_anchor),
    nr: e.edi_anchor.nr ?? "",
    bo4e: `${e.bo4e_anchor.bo}.${e.bo4e_anchor.path}`,
    transform: e.transform.type,
    status: e.status,
    entry: e,
  }));
}

/** Filtert Zeilen über Review-Filter UND Global-Suche (beide UND-verknüpft).
 * Die Suche ist ein case-insensitiver Substring-Match über anchor/nr/bo4e/
 * transform/status. */
export function applyMappingFilters(
  rows: MappingRow[],
  opts: { rowFilter: RowFilter; searchText: string },
): MappingRow[] {
  let out = rows;
  if (opts.rowFilter === "needs_review") {
    out = out.filter((r) => r.status === "needs_review" || r.transform === "manual");
  }
  const needle = opts.searchText.trim().toLowerCase();
  if (needle) {
    out = out.filter((r) =>
      [r.anchor, r.nr, r.bo4e, r.transform, r.status].some((v) =>
        v.toLowerCase().includes(needle),
      ),
    );
  }
  return out;
}
